import React, { useState } from 'react';

import { Modal, Button } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import { useGetTrustyIndustries } from 'data/v3/trusty';
import {
  useCreatePublicProfile,
  useEditPublicProfile,
  useGetPublicProfiles,
  type CreatePublicProfileParams,
} from 'data/v3/publicProfiles';
import { useGetStates } from 'data/v1/regions/useGetStates';
import toast from 'helpers/toast';
import { useModal } from 'components/shared/hooks';
import { Steven } from 'helpers/steven';

import {
  GoogleSearchStep,
  GoogleSearchStepFooterActions,
} from './GoogleSearchStep';
import {
  CompanyAddressStep,
  CompanyAddressStepFooterActions,
} from './CompanyAddressStep';
import { CompanyInfoStep, CompanyInfoFooterActions } from './CompanyInfoStep';
import { useProfileForm, type UseProfileFormReturn } from '../useProfileForm';
import { ModalHeader } from '../CommonComponents';

export type CreatePublicProfileModalProps = {};

type ModalOptions = number;

const deriveModalContent = (
  name: ModalOptions,
  props: {
    formMethods: UseProfileFormReturn;
    nextStep: () => void;
    onFinish: () => void;
    isSubmitting: boolean;
  },
) => {
  const googlePlaceId = props.formMethods.watch('googlePlaceId');
  const hasGooglePlaceId = !!googlePlaceId?.trim();

  switch (name) {
    case 1:
      return {
        title: '',
        subtitle: '',
        content: (
          <GoogleSearchStep
            formMethods={props.formMethods}
            nextStep={props.nextStep}
          />
        ),
        footerActions: (
          <GoogleSearchStepFooterActions
            nextStep={props.nextStep}
            hasGooglePlaceId={hasGooglePlaceId}
          />
        ),
      };
    case 2:
      return {
        title: translate('portfolio.profile.setupStep2'),
        subtitle: translate('portfolio.profile.subheader2'),
        content: <CompanyAddressStep formMethods={props.formMethods} />,
        footerActions: (
          <CompanyAddressStepFooterActions
            formMethods={props.formMethods}
            nextStep={props.nextStep}
          />
        ),
      };
    case 3:
      return {
        title: translate('portfolio.profile.setupStep3'),
        subtitle: translate('portfolio.profile.subheader3'),
        content: <CompanyInfoStep formMethods={props.formMethods} />,
        footerActions: (
          <CompanyInfoFooterActions
            onFinish={props.onFinish}
            isSubmitting={props.isSubmitting}
            isValid={props.formMethods.formState.isValid}
          />
        ),
      };
    default:
      return {
        title: '',
        subtitle: '',
        content: null,
        footerActions: null,
      };
  }
};

export const CreatePublicProfileModal: React.FC<
  CreatePublicProfileModalProps
> = () => {
  const { data: publicProfileData } = useGetPublicProfiles();
  const hasPublicProfiles = publicProfileData?.data?.length > 0;
  const hasOnlyIncompleteProfiles = publicProfileData?.data?.every(
    (profile) => !profile.profileSetupComplete,
  );
  const incompletePublicProfile =
    publicProfileData?.data?.find((profile) => !profile.profileSetupComplete) ||
    null;

  const { hideModal } = useModal();
  const [modalStep, setModalStep] = useState<ModalOptions>(1);
  const formMethods = useProfileForm();
  const createPublicProfile = useCreatePublicProfile({
    onError() {
      toast.error(translate('portfolio.profile.createError'));
    },
    onSuccess(response) {
      toast.success(translate('portfolio.profile.createSuccess'));
      hideModal();
      formMethods.reset();
      Steven.entityCrud({
        entity_type: 'public_profile',
        entity_id: response.data.id.toString(),
        action: 'created',
      });
    },
  });

  const editPublicProfile = useEditPublicProfile({
    profileId: incompletePublicProfile?.id,
    onError() {
      toast.error(translate('portfolio.profile.updateError'));
    },
    onSuccess(response) {
      toast.success(translate('portfolio.profile.updateSuccess'));
      hideModal();
      formMethods.reset();
      Steven.entityCrud({
        entity_type: 'public_profile',
        entity_id: response.data.id.toString(),
        action: 'updated',
      });
    },
  });

  const nextStep = () => setModalStep(modalStep + 1);
  const previousStep = () => {
    Steven.elementClicked({
      id: 'create_public_profile_back',
      content: 'common.back',
      type: 'button',
      action: 'create_public_profile_back_clicked',
    });
    setModalStep(modalStep - 1);
  };

  const onFinish = formMethods.handleSubmit(
    (data: CreatePublicProfileParams) => {
      if (hasPublicProfiles && hasOnlyIncompleteProfiles) {
        editPublicProfile.mutate(data);
        return;
      }
      createPublicProfile.mutate(data);
    },
  );

  // prefetch data needed further in the form so there aren't loading states
  useGetTrustyIndustries();
  useGetStates('US');

  const { title, subtitle, content, footerActions } = deriveModalContent(
    modalStep,
    {
      formMethods,
      nextStep,
      onFinish,
      isSubmitting: createPublicProfile.isPending,
    },
  );

  return (
    <Modal
      title={title}
      isOpen
      onClose={hideModal}
      size="small"
      headerContentAfter={subtitle}
      headerComponent={
        modalStep > 1 ? (
          <ModalHeader headerSubtitle={subtitle} headerTitle={title} />
        ) : null
      }
      footerActions={footerActions}
      footerContentBefore={
        modalStep > 1 ? (
          <Button color="subtle" onClick={previousStep}>
            {translate('common.back')}
          </Button>
        ) : null
      }
      bodyStyle={{
        paddingTop: 'var(--cc_size_spacing_xl)',
        paddingBottom: 'var(--cc_size_spacing_xl)',
        paddingRight: 'var(--cc_size_spacing_xl)',
        paddingLeft: 'var(--cc_size_spacing_xl)',
      }}
      containerStyle={{
        maxHeight: '90vh',
      }}
    >
      {content}
    </Modal>
  );
};
